import React, { FunctionComponent, useEffect, useState } from "react";
import {
  DiagramsProps,
  SalesDataType,
  SalesSumDataSet,
  SalesSumDataSets
} from "./types";
import {
  CardContent,
  FormControl,
  Grid,
  Card,
  InputLabel,
  MenuItem,
  Select,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import moment from "moment";
import "chartjs-plugin-annotation";
import "chartjs-adapter-moment";
import useStyles from "./styles";
import { DatedSalesStatistic } from "../../../../models/Statistics";
import * as StatisticsApi from "../../../../api/statisticsApi";
import { err, Result } from "../../../../result";
import { ProblemDetails } from "../../../../models/Http";
import BarChart from "../../../statistics/components/BarChart";
import { dataSetFromResponse, formatDataSet } from "./salesUtils";
import DashBoardLineChart from "./LineChart";
import { SalesSummary } from "./SalesSummary";
import { DatePicker } from "@material-ui/pickers";
import { formatCurrency } from "../../../../utils";
import { reject } from "lodash";
import { Exception } from "@zxing/library";
import { useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { Market } from "../../../../models/Market";
import { collection, onSnapshot, query } from "firebase/firestore";
import {
  firebase,
  logFirestoreError,
  dateAndTenantConstraints,
  includesStoreId
} from "../../../../firebase";
import { InclVat } from "../inclVat";
import { Language } from "../../../../models/Translation";
import { useI18n } from "../../../../hooks/useI18n";
import { asList } from "../../../../hooks/useLoadIfEmpty";

export const Sales: FunctionComponent<DiagramsProps> = ({
  selectedStores,
  comparedToText,
  title
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useI18n();

  let market =
    useSelector<AppState, Market>(s => s.currentMarket) || ({} as Market);
  const languages = asList<Language>(
    useSelector<AppState, unknown>(s => s.languages)
  );
  const currencyFormatLanguage = languages.find(
    l => l.id === market.currencyLanguageId
  );

  const locale = `${currencyFormatLanguage?.code ?? "sv"}-${
    market.countryCode || "SE"
  }`;

  const selectableSalesDataTypes: SalesDataType[] = [
    { label: "Today's Sales", value: "sum" }
  ];

  const defaultSalesSumToday = {
    today: {
      sales: [],
      uniqueUsers: 0
    }
  } as any;

  const defaultSalesSumOther = {
    other: {
      sales: [],
      uniqueUsers: 0
    }
  } as any;

  const [isLoading, setIsLoading] = useState(true);
  const [salesDataType, setSalesDataType] = useState(
    selectableSalesDataTypes[0].value
  );

  const onSalesDataTypeChanged = (e: any) => {
    setSalesDataType(e.target.value);
  };

  /**
   *  Handle sales statistics
   */
  const [today, _] = useState(moment().toDate());
  const maxDate = moment(today).subtract(1, "days").toDate();

  /* Start comparing same weekday a week ago */
  const [date1, setDate1] = useState(moment().subtract(1, "week").toDate());
  const [salesSumOtherDataSet, setSalesSumOtherDataSet] =
    useState(defaultSalesSumOther);
  const [salesSumTodayDataSet, setSalesSumTodayDataSet] =
    useState(defaultSalesSumToday);

  const getAvgStatisticsForDate = (
    start: Date,
    end: Date
  ): Promise<Result<ProblemDetails, ReadonlyArray<DatedSalesStatistic>>> => {
    return StatisticsApi.getAverageDatedReceiptStatistics(
      start,
      end,
      selectedStores.map(s => s.id)
    );
  };

  useEffect(() => {
    setIsLoading(true);
    if (!selectedStores || selectedStores.length === 0) {
      setIsLoading(false);
      return;
    }
    const todayStartDateTime = moment(today).startOf("day").toDate();
    const todayEndDateTime = moment(today).endOf("day").toDate();

    getAvgStatisticsForDate(
      moment(date1).startOf("day").toDate(),
      moment(date1).endOf("day").toDate()
    )
      .then(res => {
        const ds = dataSetFromResponse(
          res,
          todayStartDateTime,
          todayEndDateTime
        );
        setSalesSumOtherDataSet({ other: ds });
        setIsLoading(false);
      })
      .catch(e => {
        setSalesSumOtherDataSet(defaultSalesSumOther);
        setIsLoading(false);
      });
  }, [date1, selectedStores]);

  useEffect(() => {
    if (!selectedStores || selectedStores.length === 0) return;

    const storeids = selectedStores
      .filter(s => s.state === "Opened")
      .map(x => x.id);

    if (storeids.length == 0) {
      return;
    }

    const q = query(
      collection(firebase.firestore, "Sale"),
      ...dateAndTenantConstraints(selectedStores)
    );

    const unsubscribe = onSnapshot(
      q,
      snapShot => {
        let dataSet = [] as any;

        snapShot.docs.forEach(doc => {
          const data = doc.data();
          if (includesStoreId(storeids, data.StoreId)) {
            dataSet.push(data);
          }
        });

        const now = new Date();
        const todayStartDateTime = moment(today).startOf("day").toDate();
        let updatedTodayDataSet = formatDataSet(
          dataSet,
          todayStartDateTime,
          now
        );
        setSalesSumTodayDataSet({ today: updatedTodayDataSet });
      },
      logFirestoreError("Sale")
    );

    return () => {
      unsubscribe();
    };
  }, [selectedStores]);

  const lineChartDataConfig = {
    borderWidth: 1,
    pointRadius: 0,
    cubicInterpolationMode: "monotone",
    fill: true,
    tension: 0.4
  };

  const getAnnotationValueForCurrentTime = () => {
    if (!salesSumTodayDataSet.today.sales.length) {
      return null;
    }
    return moment(
      salesSumTodayDataSet.today.sales[
        salesSumTodayDataSet.today.sales.length - 1
      ].time
    ).format("YYYY-MM-DD HH:mm");
  };

  const getAnnotationsForSalesSum = () => {
    if (salesSumTodayDataSet.today.sales.length) {
      return {
        annotations: [
          {
            drawTime: "afterDatasetsDraw",
            type: "line",
            value: getAnnotationValueForCurrentTime(),
            scaleID: "x",
            borderColor: "red",
            borderWidth: 1
          }
        ]
      };
    }
    return {};
  };

  return (
    <Card>
      <CardContent className={classes.sectionContent}>
        {title}
        <Grid
          container
          direction={"row"}
          justifyContent={"space-evenly"}
          alignItems={"flex-start"}
          spacing={useMediaQuery(theme.breakpoints.down("xs")) ? 0 : 3}
        >
          <Grid item xs={12} className={classes.chartsContainer}>
            <DashBoardLineChart
              title={"Today vs Date"}
              chartData={{
                datasets: [
                  {
                    label: t("common.time.today"),
                    data: salesSumTodayDataSet.today.sales,
                    borderColor: "rgb(178,93,171)",
                    backgroundColor: "rgba(178,93,171, 0.4)"
                  },
                  {
                    label: comparedToText,
                    data: salesSumOtherDataSet.other.sales,
                    borderColor: "rgb(102,106,108)",
                    backgroundColor: "rgb(102,106,108, 0.1)"
                  }
                ]
              }}
              options={{
                ...lineChartDataConfig,
                parsing: {
                  xAxisKey: "time",
                  yAxisKey: "sum"
                },
                scales: {
                  x: {
                    type: "time",
                    time: {
                      unit: "hour",
                      displayFormats: { hour: "HH:mm" }
                    },
                    grid: { display: false },
                    ticks: {
                      beginAtZero: true,
                      maxTicksLimit: 10
                    }
                  },
                  y: {
                    grid: { display: false }
                  }
                },
                plugins: {
                  annotation: getAnnotationsForSalesSum(),
                  tooltip: {
                    callbacks: {
                      title: function (data: any[]) {
                        return data.map(
                          d =>
                            `${d.dataset.label} ${moment(d.raw.time).format(
                              "HH:mm"
                            )}`
                        );
                      },
                      label: function (data: any) {
                        return `Sum: ${formatCurrency(
                          data.raw.sum,
                          market.currency,
                          locale
                        )}`;
                      }
                    }
                  }
                }
              }}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>
        <InclVat />
      </CardContent>
    </Card>
  );
};
